import SearchIcon from "@mui/icons-material/Search";
import SwapHorizOutlined from "@mui/icons-material/SwapHorizOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { getEmergencyAlert } from "api/services";
import { EmptyDashboard } from "assets";
import EmptyPage from "components/EmptyPage";
import RowActions from "components/RowActions";
import Table from "components/TableComponent";
import textCapitalization from "components/textCapitalization";
import moment from "moment";
import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { StyledFlexBox } from "styles";
import UpdateStatusDialog from "./UpdateStatusDialog";

const statusColor: any = {
  PENDING: "rgb(242,151,31)",
  CONTACTED: "rgb(0,0,255)",
  VISITED: "rgb(79,190,166)",
  COMPLETED: "rgb(76,175,80)",
};

const statusBg: any = {
  PENDING: "rgb(242,151,31,0.1)",
  CONTACTED: "rgb(0,0,255,0.1)",
  VISITED: "rgb(79,190,166,0.1)",
  COMPLETED: "rgb(76,175,80,0.1)",
};

const Ellipsis = ({ text, max = 220 }: { text: string; max?: number }) => (
  <Tooltip title={text} placement="top" arrow>
    <Box
      component="span"
      sx={{
        display: "block",
        maxWidth: max,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </Box>
  </Tooltip>
);

function EmergencyAlerts() {
  const navigate = useNavigate();
  const [search, setSearch] = useState<any>("");
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [statusDialog, setStatusDialog] = useState<{
    open: boolean;
    id?: string;
    status?: string;
  }>({ open: false });

  const { data, isLoading } = useQuery(
    [
      "emergencyAlerts",
      {
        search: search,
        limit: limit,
        offset: page * limit,
      },
    ],
    getEmergencyAlert
  );

  const openDetail = (row: any) => navigate(`/emergency-alerts/${row?._id}`);

  const openStatus = (row: any) =>
    setStatusDialog({ open: true, id: row?._id, status: row?.status });

  const column = [
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_c: any, row: any) =>
        moment(row?.createdAt).format("DD MMM YYYY, hh:mm a"),
    },
    {
      title: "User Full Name",
      dataIndex: "userName",
      key: "userName",
      render: (_c: any, row: any) => (
        <Box component="span" sx={{ fontFamily: "albertSans_semibold" }}>
          {textCapitalization(row?.user?.name || row?.userName || "-")}
        </Box>
      ),
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: (_c: any, row: any) =>
        textCapitalization(row?.user?.country || "-"),
    },
    {
      title: "Family Member",
      dataIndex: "familyMemberName",
      key: "familyMemberName",
      render: (_c: any, row: any) =>
        textCapitalization(row?.familyMember?.name || "-"),
    },
    {
      title: "Relation",
      dataIndex: "relation",
      key: "relation",
      render: (_c: any, row: any) =>
        textCapitalization(row?.familyMember?.relation || "-"),
    },
    {
      title: "Address",
      dataIndex: "location",
      key: "location",
      render: (_c: any, row: any) =>
        row?.location ? <Ellipsis text={row.location} /> : "-",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (_c: any, row: any) =>
        row?.reason ? <Ellipsis text={row.reason} max={160} /> : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_c: any, row: any) => {
        const s = row?.status || "PENDING";
        return (
          <Box
            sx={{
              py: 0.75,
              px: 1.5,
              borderRadius: "8px",
              display: "inline-block",
              fontFamily: "albertSans_medium",
              fontSize: "13px",
              textAlign: "center",
              background: statusBg[s] || statusBg.PENDING,
              color: statusColor[s] || statusColor.PENDING,
            }}
          >
            {textCapitalization(s)}
          </Box>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "_id",
      key: "_id",
      render: (_c: any, row: any) => (
        <RowActions
          actions={[
            {
              label: "View Details",
              icon: <VisibilityOutlined fontSize="small" />,
              onClick: () => openDetail(row),
            },
            {
              label: "Update Status",
              icon: <SwapHorizOutlined fontSize="small" />,
              onClick: () => openStatus(row),
            },
          ]}
        />
      ),
    },
  ];

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2.5}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="h4">Emergency Alerts</Typography>
          {typeof data?.data?.count === "number" && (
            <Box
              sx={{
                py: 0.5,
                px: 1.5,
                borderRadius: "20px",
                background: "#EEF0FA",
                color: "#1E2A8A",
                fontFamily: "albertSans_semibold",
                fontSize: "13px",
              }}
            >
              {data?.data?.count} total
            </Box>
          )}
        </Box>
      </Box>
      <StyledFlexBox width="600px" mb={2} gap={2}>
        <TextField
          label="Search"
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
      </StyledFlexBox>
      {!isLoading && data?.data?.count === 0 ? (
        <EmptyPage
          image={EmptyDashboard}
          title="There are no Emergency Alerts"
        />
      ) : (
        <Table
          columns={column}
          headAlign={"left"}
          tableAlign={"left"}
          dataSource={data?.data?.data}
          loading={isLoading}
          onRowClick={openDetail}
          pagination={{
            page: page,
            rowsPerPage: limit,
            count: data?.data?.count,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handleRowsPerPageChange,
          }}
        />
      )}

      <UpdateStatusDialog
        open={statusDialog.open}
        alertId={statusDialog.id}
        currentStatus={statusDialog.status}
        onClose={() => setStatusDialog({ open: false })}
      />
    </Box>
  );
}

export default EmergencyAlerts;
